import React, { useEffect, useRef } from 'react';
import type { LightPoint } from '../../types/ambilight';

interface AmbientWallCanvasProps {
  points: LightPoint[];
  screenRectRef?: React.RefObject<HTMLDivElement | null>;
  className?: string;
}

export const AmbientWallCanvas: React.FC<AmbientWallCanvasProps> = ({
  points,
  screenRectRef,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(performance.now());

  // Uniform locations cache
  const uniformsRef = useRef<{
    resolution: WebGLUniformLocation | null;
    time: WebGLUniformLocation | null;
    colors: WebGLUniformLocation | null;
    positions: WebGLUniformLocation | null;
    brightness: WebGLUniformLocation | null;
    screenRect: WebGLUniformLocation | null;
  }>({
    resolution: null,
    time: null,
    colors: null,
    positions: null,
    brightness: null,
    screenRect: null,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      depth: false,
      preserveDrawingBuffer: false,
      premultipliedAlpha: true,
    });

    if (!gl) {
      console.warn('WebGL no disponible para AmbientWallCanvas');
      return;
    }

    glRef.current = gl;

    const vsSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Shader WebGL de Iluminación Ambiental Vívida y Continua (8 Focos)
    const fsSource = `
      precision highp float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec3 u_colors[9];
      uniform vec2 u_positions[9];
      uniform float u_brightness[9];
      uniform vec4 u_screenRect; // [min_x, min_y, max_x, max_y] en UV 0..1

      // Conversión sRGB a Luz Lineal para fusión fotométrica exacta
      vec3 toLinear(vec3 c) {
        return pow(max(c, vec3(0.0)), vec3(2.2));
      }
      // Conversión Luz Lineal a sRGB con curva Gamma
      vec3 toGamma(vec3 c) {
        return pow(max(c, vec3(0.0)), vec3(1.0 / 2.2));
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution;

        float sX0 = u_screenRect.x;
        float sY0 = u_screenRect.y;
        float sX1 = u_screenRect.z;
        float sY1 = u_screenRect.w;

        // Oclusión total bajo el monitor físico (sin fugas ni resplandores internos)
        if (uv.x >= sX0 && uv.x <= sX1 && uv.y >= sY0 && uv.y <= sY1) {
          gl_FragColor = vec4(0.0);
          return;
        }

        vec2 screenCenter = vec2((sX0 + sX1) * 0.5, (sY0 + sY1) * 0.5);
        vec3 accumulated = vec3(0.0);

        // Fusión de iluminación desde las 9 fuentes de luz (8 perimetrales + 1 pantalla completa)
        for (int i = 0; i < 9; i++) {
          float br = u_brightness[i];
          if (br <= 0.005) continue;

          vec2 p = u_positions[i];
          vec3 col = toLinear(u_colors[i]);

          vec2 delta = uv - p;
          float dist = length(delta);

          // Si es el punto 'all' (índice 8), proyecta aura omnidireccional simétrica
          float directional = 1.0;
          if (i < 8) {
            vec2 outward = normalize(p - screenCenter);
            vec2 sampleDir = (dist > 0.001) ? normalize(delta) : outward;
            float dotVal = dot(sampleDir, outward);
            directional = 0.35 + 0.65 * max(0.0, dotVal + 0.25);
          }

          // Componentes de luz física en la pared con caída natural rápida
          float core = exp(-dist * 11.0) * 1.5 * directional;
          float wash = exp(-dist * 4.5) * 0.8 * directional;
          float bloom = exp(-dist * 2.2) * 0.25;

          float intensity = (core + wash + bloom) * br;
          accumulated += col * intensity;
        }

        // Si no hay luz acumulada en este punto de la pared, píxel 100% transparente
        float totalLight = max(accumulated.r, max(accumulated.g, accumulated.b));
        if (totalLight <= 0.002) {
          gl_FragColor = vec4(0.0);
          return;
        }

        // Curva tonal filmic Reinhard suave: no satura en blanco deslavado, preserva el color puro
        vec3 exposed = accumulated / (vec3(1.0) + accumulated * 0.9);
        vec3 finalColor = toGamma(exposed);

        // Suave desvanecimiento natural hacia los bordes exteriores del lienzo
        float edgeMarginX = smoothstep(0.0, 0.08, min(uv.x, 1.0 - uv.x));
        float edgeMarginY = smoothstep(0.0, 0.08, min(uv.y, 1.0 - uv.y));
        float edgeFade = edgeMarginX * edgeMarginY;

        // La opacidad (alpha) depende directamente de la luz emitida (zonas oscuras = fondo negro transparente)
        float alpha = clamp(totalLight * 1.4, 0.0, 0.92) * edgeFade;

        gl_FragColor = vec4(finalColor * alpha, alpha);
      }
    `;

    function createShader(glCtx: WebGLRenderingContext, type: number, source: string) {
      const shader = glCtx.createShader(type);
      if (!shader) return null;
      glCtx.shaderSource(shader, source);
      glCtx.compileShader(shader);
      if (!glCtx.getShaderParameter(shader, glCtx.COMPILE_STATUS)) {
        console.error('Error compilando shader WebGL:', glCtx.getShaderInfoLog(shader));
        glCtx.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vs = createShader(gl, gl.VERTEX_SHADER, vsSource);
    const fs = createShader(gl, gl.FRAGMENT_SHADER, fsSource);
    if (!vs || !fs) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Error enlazando programa WebGL:', gl.getProgramInfoLog(program));
      return;
    }

    programRef.current = program;
    gl.useProgram(program);

    // Quad
    const posBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1.0, -1.0,
         1.0, -1.0,
        -1.0,  1.0,
        -1.0,  1.0,
         1.0, -1.0,
         1.0,  1.0,
      ]),
      gl.STATIC_DRAW
    );

    const aPos = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Uniforms
    uniformsRef.current = {
      resolution: gl.getUniformLocation(program, 'u_resolution'),
      time: gl.getUniformLocation(program, 'u_time'),
      colors: gl.getUniformLocation(program, 'u_colors'),
      positions: gl.getUniformLocation(program, 'u_positions'),
      brightness: gl.getUniformLocation(program, 'u_brightness'),
      screenRect: gl.getUniformLocation(program, 'u_screenRect'),
    };

    const resizeCanvas = () => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
    };

    resizeCanvas();
    const ro = new ResizeObserver(() => resizeCanvas());
    ro.observe(canvas);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const pointsRef = useRef(points);
  pointsRef.current = points;

  // Bucle de renderizado continuo a 60 FPS
  useEffect(() => {
    const gl = glRef.current;
    const program = programRef.current;
    const canvas = canvasRef.current;
    if (!gl || !program || !canvas) return;

    const colorsArr = new Float32Array(9 * 3);
    const posArr = new Float32Array(9 * 2);
    const brightArr = new Float32Array(9);

    const pointOrder = [
      'top-left',     // 0
      'top',          // 1
      'top-right',    // 2
      'right',        // 3
      'bottom-right', // 4
      'bottom',       // 5
      'bottom-left',  // 6
      'left',         // 7
      'all',          // 8 (Toda la Pantalla)
    ];

    const render = () => {
      const now = performance.now();
      const elapsed = (now - startTimeRef.current) * 0.001;

      gl.useProgram(program);

      const canvasRect = canvas.getBoundingClientRect();
      let sX0 = 0.20;
      let sY0 = 0.20;
      let sX1 = 0.80;
      let sY1 = 0.80;

      if (screenRectRef && screenRectRef.current && canvasRect.width > 0 && canvasRect.height > 0) {
        const sRect = screenRectRef.current.getBoundingClientRect();
        sX0 = Math.max(0.01, Math.min(0.99, (sRect.left - canvasRect.left) / canvasRect.width));
        sY0 = Math.max(0.01, Math.min(0.99, 1.0 - (sRect.bottom - canvasRect.top) / canvasRect.height));
        sX1 = Math.max(0.01, Math.min(0.99, (sRect.right - canvasRect.left) / canvasRect.width));
        sY1 = Math.max(0.01, Math.min(0.99, 1.0 - (sRect.top - canvasRect.top) / canvasRect.height));
      }

      // Offset leve hacia el exterior del bisel para coincidir con la posición de los focos físicos
      const offset = 0.024;

      // 9 posiciones exactas coincidiendo con los focos flotantes
      // 0: top-left (18% X, arriba)
      posArr[0] = sX0 + 0.18 * (sX1 - sX0);
      posArr[1] = sY1 + offset;

      // 1: top (50% X, arriba)
      posArr[2] = sX0 + 0.50 * (sX1 - sX0);
      posArr[3] = sY1 + offset;

      // 2: top-right (82% X, arriba)
      posArr[4] = sX0 + 0.82 * (sX1 - sX0);
      posArr[5] = sY1 + offset;

      // 3: right (derecha, 50% Y)
      posArr[6] = sX1 + offset;
      posArr[7] = sY0 + 0.50 * (sY1 - sY0);

      // 4: bottom-right (82% X, abajo)
      posArr[8] = sX0 + 0.82 * (sX1 - sX0);
      posArr[9] = sY0 - offset;

      // 5: bottom (50% X, abajo)
      posArr[10] = sX0 + 0.50 * (sX1 - sX0);
      posArr[11] = sY0 - offset;

      // 6: bottom-left (18% X, abajo)
      posArr[12] = sX0 + 0.18 * (sX1 - sX0);
      posArr[13] = sY0 - offset;

      // 7: left (izquierda, 50% Y)
      posArr[14] = sX0 - offset;
      posArr[15] = sY0 + 0.50 * (sY1 - sY0);

      // 8: all (centro de la pantalla)
      posArr[16] = (sX0 + sX1) * 0.5;
      posArr[17] = (sY0 + sY1) * 0.5;

      // Rellenar colores y brillo según luminancia real y asignación física
      let anyLightActive = false;
      pointOrder.forEach((id, idx) => {
        const pt = pointsRef.current.find((p) => p.id === id);
        if (pt) {
          // Si el punto NO tiene foco físico asignado, no emite luz en la pared
          const hasBulb = pt.assignedBulbId !== null;
          if (!hasBulb) {
            colorsArr[idx * 3] = 0;
            colorsArr[idx * 3 + 1] = 0;
            colorsArr[idx * 3 + 2] = 0;
            brightArr[idx] = 0;
            return;
          }

          colorsArr[idx * 3] = pt.color.r / 255;
          colorsArr[idx * 3 + 1] = pt.color.g / 255;
          colorsArr[idx * 3 + 2] = pt.color.b / 255;

          const lum = typeof pt.brightness === 'number' ? pt.brightness / 100 : 0;
          const dim = typeof pt.dimming === 'number' ? pt.dimming / 100 : 0.7;
          const effectiveBright = Math.max(0.0, lum * dim);
          brightArr[idx] = effectiveBright;
          if (effectiveBright > 0.01) anyLightActive = true;
        }
      });

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // Si no hay ningún foco físico emitiendo luz, queda 100% transparente (sin velos ni cajas blancas)
      if (!anyLightActive) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      const u = uniformsRef.current;
      if (u.resolution) gl.uniform2f(u.resolution, canvas.width, canvas.height);
      if (u.time) gl.uniform1f(u.time, elapsed);
      if (u.colors) gl.uniform3fv(u.colors, colorsArr);
      if (u.positions) gl.uniform2fv(u.positions, posArr);
      if (u.brightness) gl.uniform1fv(u.brightness, brightArr);
      if (u.screenRect) gl.uniform4f(u.screenRect, sX0, sY0, sX1, sY1);

      gl.drawArrays(gl.TRIANGLES, 0, 6);

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [screenRectRef]);

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 w-full h-full ${className}`}
      style={{
        filter: 'blur(10px)',
        opacity: 1.0,
      }}
    />
  );
};
